import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DownloadIcon from "@mui/icons-material/Download";
import TableContainer from "../../Components/TableContainer";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import useDebounce from "../../Components/useDebounce";
import CustomSearchBox from "../../Components/CustomSearch";
import PathList from "../../Common/PathList";
import { categoryLabel, engineLabel } from "./walkIssueMeta";

const ReportedWalkIssues = () => {
  const pageSize = 15;
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalData, setTotalData] = useState(0);
  const debouncedSearch = useDebounce(search, 500);

  const handlePageChange = (newValue) => {
    setPage(newValue);
  };

  // Download the gzipped diagnostic slice for a report (backend gunzips → .txt).
  const handleDownloadDiag = async (row) => {
    try {
      const res = await AxiosInstance.get(
        `${ApiList.GET_WALK_ISSUES}/${row.id}/diag`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `walk-${row.activity_id}-diag.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("diag download failed", error);
    }
  };

  const getWalkIssuesList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(ApiList.GET_WALK_ISSUES, {
        params: {
          count: page * pageSize,
          search: debouncedSearch,
        },
      });
      if (response?.data?.s === 1) {
        const formatted = response?.data?.r?.map((item, index) => ({
          rowid: index + 1 + page * pageSize,
          ...item,
        }));
        setData(formatted);
        setTotalData(response?.data?.c);
      } else {
        setData([]);
        setTotalData(0);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const columns = [
    {
      field: "rowid",
      headerName: "Sr.No",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "created_at",
      headerName: "Reported",
      flex: 1,
      minWidth: 160,
      headerClassName: "leftAlignHeader",
      cellClassName: "leftAlign",
      renderCell: (params) =>
        params?.row?.created_at
          ? moment(params.row.created_at).format("MMM D, YYYY h:mm A")
          : "—",
    },
    {
      field: "category",
      headerName: "Issue",
      flex: 1,
      minWidth: 220,
      headerClassName: "leftAlignHeader",
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const label = categoryLabel(params?.row?.category);
        return <Tooltip title={label}>{label}</Tooltip>;
      },
    },
    {
      field: "tracking_engine",
      headerName: "Engine",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => engineLabel(params?.row?.tracking_engine),
    },
    {
      field: "reporter",
      headerName: "Reporter",
      flex: 1,
      minWidth: 160,
      headerClassName: "leftAlignHeader",
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const name = `${params?.row?.reporter_fname || ""} ${
          params?.row?.reporter_lname || ""
        }`.trim();
        const display = name || "—";
        const truncated =
          display.length > 22 ? `${display.substring(0, 22)}...` : display;
        return <Tooltip title={display}>{truncated}</Tooltip>;
      },
    },
    {
      field: "reporter_email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      headerClassName: "leftAlignHeader",
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const email = params?.row?.reporter_email || "—";
        const truncated =
          email.length > 24 ? `${email.substring(0, 24)}...` : email;
        return <Tooltip title={email}>{truncated}</Tooltip>;
      },
    },
    {
      field: "note",
      headerName: "Note",
      flex: 1,
      minWidth: 200,
      headerClassName: "leftAlignHeader",
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const note = params?.row?.note;
        if (!note) return "—";
        const truncated = note.length > 30 ? `${note.substring(0, 30)}...` : note;
        return <Tooltip title={note}>{truncated}</Tooltip>;
      },
    },
    {
      field: "Action",
      headerName: "Action",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
          <IconButton
            onClick={() =>
              navigate(PathList.ReportedWalkIssueDetails, {
                state: { report: params?.row },
              })
            }
          >
            <RemoveRedEyeIcon sx={{ color: "#b9cc03" }} />
          </IconButton>
          {params?.row?.has_diag ? (
            <Tooltip title="Download diagnostic log">
              <IconButton onClick={() => handleDownloadDiag(params.row)}>
                <DownloadIcon sx={{ color: "#b9cc03" }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    getWalkIssuesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        background: "#B9CC0310",
        overflow: "hidden",
      }}
    >
      <Box sx={{ width: "100%", p: 2, background: "#B9CC0310" }}>
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "28px" },
            fontWeight: "300",
            color: "#000",
          }}
        >
          Reported Walk Issues
        </Typography>
      </Box>
      <Box sx={{ width: "100%", p: 2 }}>
        <Box sx={{ width: "400px" }}>
          <CustomSearchBox
            placeholder={"Search by reporter name or email"}
            onChange={(e) => setSearch(e?.target?.value)}
            onClear={() => setSearch("")}
          />
        </Box>
      </Box>
      <Box sx={{ width: "100%", height: "calc(100vh - 158.67px)", p: 2 }}>
        <TableContainer
          columns={columns}
          rows={data}
          handlePageChange={handlePageChange}
          page={page}
          loading={loading}
          totalData={totalData}
          pageSize={pageSize}
        />
      </Box>
    </Box>
  );
};

export default ReportedWalkIssues;
